/**
 * The bench apparatus a student chooses between — the four stands
 * (standTypes.ts) and the two burners (burnerTypes.ts) — drawn in the same
 * textbook-diagram language as the vessels: thin black outlines, flat fills,
 * no shading.
 *
 * Local frame for everything here: origin at the column centre on the bench
 * line, y growing upward as NEGATIVE (the scene's convention).
 *
 * Same projection as the scene, from projection.ts: the viewer stands a little
 * above the bench, so anything LYING FLAT here is foreshortened rather than
 * collapsed to a line — a gauze is a plate you see the top of, a burner's base
 * and a clamp's ring are ellipses, a rack's boards show their top edges. This
 * file was the last thing on the bench still drawn at the old eye level, and a
 * flat gauze under a vessel whose mouth is an ellipse is exactly the mismatch
 * that reads as a broken 3D render.
 *
 * Uprights are unchanged and need no tilt: a rod, a leg, a barrel is seen
 * side-on at any eye height.
 */
import type { BurnerType } from '../burnerTypes';
import {
  STEEL, STEEL_LIGHT, BRASS, WOOD, WOOD_DARK, WOOD_LIGHT, FLAME_OUTER, FLAME_INNER,
} from '../glassStyle';
import { VIEW_TILT, ellipsePath, frontArcPath, backArcPath, bandPath, discPath } from '../projection';
import { CRADLE_SINK } from '../benchLayout';

/** Outlines across the whole bench. Dark enough to draw apparatus edges
 *  against pale glass, light enough not to vanish into the navy page. */
export const LINE = '#1b2233';

/** Above this grip half-width a jaw clamp makes no sense — a wide, straight
 *  vessel is held by a ring the vessel sits inside, not gripped by the neck. */
const RING_CLAMP_THRESHOLD_PX = 36;

/** How far out the collar sits from the glass it holds. A jaw hugs tighter
 *  than a ring, which the vessel merely rests in. */
function collarRadius(clamp: RodClamp): number {
  return clamp.gripHalfWidthPx + (clamp.gripHalfWidthPx > RING_CLAMP_THRESHOLD_PX ? 6 : 4);
}

/** A ring is wire; a jaw is a machined pad, and reads heavier. */
function collarWeight(clamp: RodClamp): number {
  return clamp.gripHalfWidthPx > RING_CLAMP_THRESHOLD_PX ? 4.5 : 5.5;
}

/** Stands draw in two passes: everything behind the glassware, then the parts
 *  that physically sit in front of it (a clamp's jaw, a rack's front rail). */
export type Layer = 'behind' | 'front';

/** The flame's own drawn height, before scaling. */
const FLAME_ART_HEIGHT = 38;

export function Flame({ scaleX, scaleY }: { scaleX: number; scaleY: number }): React.JSX.Element {
  // The scale lives on an OUTER group: a CSS transform animation REPLACES an
  // SVG transform attribute on the same element rather than composing with it,
  // so a scale attribute here would be dropped the moment the flicker starts.
  return (
    <g transform={`scale(${scaleX}, ${scaleY})`}>
      <g className="chemlab-flame">
        <path d="M 0,2 C -10,-14 -7,-26 0,-36 C 7,-26 10,-14 0,2 Z" fill={FLAME_OUTER} />
        <path d="M 0,-1 C -5,-12 -3.5,-20 0,-27 C 3.5,-20 5,-12 0,-1 Z" fill={FLAME_INNER} />
      </g>
    </g>
  );
}

/**
 * Sizes the flame to how hard the burner is being driven (`heat`, 0-1): a low
 * modest cone well clear of the glass, growing to a tall one that spreads and
 * licks the vessel's floor. `gap` is the distance from the burner's top to
 * whatever it is heating, so the flame reaches its target no matter how high
 * the stand holds it — and never further, since a flame climbing over the
 * glassware would read as the vessel being on fire.
 */
function flameScale(heat: number, gap: number, ceiling = Infinity): { scaleX: number; scaleY: number } | null {
  if (heat <= 0) return null;
  const full = Math.min(ceiling, (gap + 5) / FLAME_ART_HEIGHT);
  return {
    // Driven hard, a flame does not just get taller, it fans out — and that
    // spread is most of what separates "warming" from "boiling this" at a
    // glance, more than the height does.
    scaleX: 0.7 + 0.95 * heat,
    // Past 1.0 at the top of the range, so the tip does not merely arrive at
    // the vessel's floor but LICKS it and flattens against it. Reaching
    // exactly, which is what the old curve did, reads as falling short: the
    // last few pixels are behind the gauze or the glass anyway.
    scaleY: Math.max(0.36, full * (0.38 + 0.8 * heat)),
  };
}

/** The physical burner object, present whether lit or not (a placed-but-unlit
 *  burner still has a body).
 *
 *  A Bunsen's barrel STRETCHES to `flameY` so its flame meets whatever the
 *  stand raised the vessel to. A spirit lamp cannot: it is a fixed squat
 *  reservoir sitting on the bench, so a vessel clamped high above it simply
 *  gets a flame that falls short — which is the true answer, and the reason
 *  a real bench reaches for the Bunsen when the vessel is up on a stand. */
export function Burner2D({ type, flameY, heat, layer }: {
  type: BurnerType; flameY: number; heat: number; layer: Layer;
}): React.JSX.Element | null {
  if (type === 'none') return null;

  // Burners draw entirely behind the glassware: a burner is under the vessel,
  // and a flame painted over the glass reads as the vessel being on fire.
  if (layer === 'front') return null;

  if (type === 'spirit-lamp') {
    const BODY_H = 26;
    const wickY = -BODY_H - 11;
    // Capped lower than the Bunsen's: a spirit lamp is the gentle flame, and
    // it should look like one even at full boil.
    const scale = flameScale(heat, wickY - flameY, 0.95);
    return (
      <g>
        {scale && (
          <g transform={`translate(0, ${wickY})`}>
            <Flame scaleX={scale.scaleX * 0.8} scaleY={scale.scaleY} />
          </g>
        )}
        {/* Squat reservoir — a shouldered trapezoid, wide at the bench, closed
            underneath by the near half of the circle it actually stands on. */}
        <path
          d={`M -18,0 L 18,0 L 13,${-BODY_H * 0.7} L 9,${-BODY_H} L -9,${-BODY_H} L -13,${-BODY_H * 0.7} Z`}
          fill={STEEL} stroke={LINE} strokeWidth={1.5} strokeLinejoin="round"
        />
        <path
          d={discPath(18 * VIEW_TILT, 18, 18 * VIEW_TILT)}
          fill={STEEL} stroke={LINE} strokeWidth={1.5} strokeLinejoin="round"
        />
        {/* A lit edge down the left, the way every other object here is lit. */}
        <path d={`M -14,${-BODY_H * 0.55} L -10.5,${-BODY_H * 0.9} L -7,${-BODY_H * 0.9} L -10,${-BODY_H * 0.5} Z`} fill={STEEL_LIGHT} />
        <rect x={-18} y={-5} width={36} height={5} fill={STEEL_LIGHT} opacity={0.45} />
        {/* Brass collar + wick — the collar is a short cylinder, so its top
            and bottom are circles like everything else here. */}
        <path d={bandPath(-BODY_H - 8, -BODY_H + 1, 7, 7 * VIEW_TILT)} fill={BRASS} stroke={LINE} strokeWidth={1.2} strokeLinejoin="round" />
        <path d={frontArcPath(-BODY_H - 8, 7, 7 * VIEW_TILT)} fill="none" stroke={LINE} strokeWidth={1} opacity={0.5} />
        <path d={ellipsePath(-BODY_H - 12, 2.5, 2.5 * VIEW_TILT)} fill="#6b5436" />
        <rect x={-2.5} y={-BODY_H - 12} width={5} height={5} fill="#6b5436" />
      </g>
    );
  }

  // Bunsen: a splayed base, brass collar, and a barrel that takes up most of
  // the gap to whatever it is heating — the flame bridges the rest.
  const barrelTop = Math.min(-24, flameY * 0.42);
  const scale = flameScale(heat, barrelTop - flameY);
  return (
    <g>
      {scale && <g transform={`translate(0, ${barrelTop})`}><Flame {...scale} /></g>}
      {/* Splayed base: a disc, closed underneath by the near half of its own
          circle rather than cut off flat at the bench line. */}
      <path d="M -17,0 L 17,0 L 9,-9 L -9,-9 Z" fill={STEEL} stroke={LINE} strokeWidth={1.5} strokeLinejoin="round" />
      <path d={discPath(17 * VIEW_TILT, 17, 17 * VIEW_TILT)} fill={STEEL} stroke={LINE} strokeWidth={1.5} strokeLinejoin="round" />
      <rect x={-4.5} y={barrelTop} width={9} height={-barrelTop} rx={1} fill={STEEL} stroke={LINE} strokeWidth={1.5} />
      <rect x={-2.5} y={barrelTop + 2} width={2} height={-barrelTop - 6} fill={STEEL_LIGHT} opacity={0.7} />
      <path
        d={bandPath(barrelTop * 0.42, barrelTop * 0.42 + barrelTop * -0.26, 6, 6 * VIEW_TILT)}
        fill={BRASS} stroke={LINE} strokeWidth={1} strokeLinejoin="round"
      />
    </g>
  );
}

/**
 * Tripod legs and what sits on top of them, which is not the same thing twice.
 *
 * A real tripod is three legs and a RING. What goes on the ring depends on what
 * is being heated: a flat-bottomed vessel gets a wire gauze laid over it and
 * stands on that, and a round-bottomed flask gets no gauze at all — it would
 * roll off one — and rests IN the ring, gripped below its equator. The layout
 * already knows the difference (benchLayout's CRADLE_SINK sinks a round bottom
 * into the ring); this is the drawing catching up with it. Drawn as a gauze,
 * the plate cut straight across the flask's belly and the whole thing read as a
 * schematic rather than as apparatus.
 *
 * The cradle takes both layers: the far half of the ring passes behind the
 * glass and the near half in front of it, which is the entire cue that the
 * flask is sitting IN something rather than beside it.
 */
export function Tripod2D({ ringY, ringRadiusPx, cradle, layer }: {
  ringY: number;
  ringRadiusPx: number;
  /** The vessel has no flat base: no gauze, and the ring holds it directly. */
  cradle: boolean;
  layer: Layer;
}): React.JSX.Element | null {
  const legSpread = ringRadiusPx * 1.35;

  if (cradle) {
    /* Where the sphere actually touches the ring. It sinks CRADLE_SINK of its
       radius below the ring's plane, so the circle of contact is narrower than
       the flask is wide — which is why the flask visibly overhangs the ring it
       stands in, and why its bottom shows underneath. */
    const rx = ringRadiusPx * Math.sqrt(1 - (1 - CRADLE_SINK) ** 2);
    const ry = rx * VIEW_TILT;
    const RING_W = 5;
    if (layer === 'front') {
      return (
        <path
          d={frontArcPath(ringY, rx, ry)}
          fill="none" stroke={STEEL} strokeWidth={RING_W} strokeLinecap="round"
        />
      );
    }
    return (
      <g>
        {/* Legs from the ring's own widest points — where a tripod's legs are
            welded — splaying out to the bench. The third is directly behind,
            hidden by the flask and the burner, as it is in elevation. */}
        <g stroke={STEEL} strokeWidth={4.5} fill="none" strokeLinecap="round">
          <line x1={-legSpread} y1={-1} x2={-rx} y2={ringY} />
          <line x1={legSpread} y1={-1} x2={rx} y2={ringY} />
        </g>
        <path d={backArcPath(ringY, rx, ry)} fill="none" stroke={STEEL} strokeWidth={RING_W} strokeLinecap="round" />
      </g>
    );
  }

  if (layer === 'front') return null;
  // Wider than the vessel that stands on it — a gauze exactly the base's size
  // is a gauze you cannot see.
  const gaugeHalf = ringRadiusPx * 1.28;
  const GAUZE_H = 4;
  // A gauze is a SQUARE plate lying flat, so from above you see its top face:
  // as deep as it is wide, foreshortened. Drawn as a bar it was the flattest
  // thing on the bench, and it sits directly under a vessel whose mouth is an
  // ellipse — the two could not have been more directly compared.
  //
  // The face STRADDLES the line the vessel stands on rather than sitting above
  // it: the vessel is in the middle of the plate, not at its back edge, so the
  // far half is hidden behind the glass (the tripod draws in the 'behind'
  // layer) and the near half shows in front of it. Hung above the line, the
  // plate covered the bottom of whatever was standing on it.
  // Capped. A gauze really is square, so strictly this should be the full
  // width foreshortened — but at this tilt that is a plate deep enough to bury
  // the burner underneath it, and the burner is the thing the student is
  // actually adjusting. Truth loses to the flame here, deliberately.
  const faceDepth = Math.min(24, gaugeHalf * 2 * VIEW_TILT);
  const faceTop = ringY - faceDepth / 2;
  return (
    <g>
      {/* Two legs splaying out to the bench — the third is directly behind and
          hidden, which is exactly what a tripod does in elevation. */}
      <g stroke={STEEL} strokeWidth={4.5} fill="none" strokeLinecap="round">
        <line x1={-legSpread} y1={-1} x2={-ringRadiusPx * 0.45} y2={ringY} />
        <line x1={legSpread} y1={-1} x2={ringRadiusPx * 0.45} y2={ringY} />
      </g>
      {/* Its top face, then the front edge that gives the plate thickness. A
          gauze is woven wire, not sheet steel, so the flame under it shows
          THROUGH rather than being hidden by it — which is also the only way
          a burner stays visible once the plate has real depth. */}
      <rect
        x={-gaugeHalf} y={faceTop} width={gaugeHalf * 2} height={faceDepth + GAUZE_H} rx={2}
        fill={STEEL} fillOpacity={0.82} stroke={LINE} strokeWidth={1.5}
      />
      <rect x={-gaugeHalf} y={faceTop} width={gaugeHalf * 2} height={faceDepth} rx={2} fill={STEEL_LIGHT} opacity={0.3} />
      {/* The mesh, on the half of the face that is actually in view. */}
      <g stroke={STEEL_LIGHT} strokeWidth={1.4} opacity={0.7}>
        {[0.62, 0.82].map(t => (
          <line key={t} x1={-gaugeHalf * 0.86} y1={faceTop + faceDepth * t} x2={gaugeHalf * 0.86} y2={faceTop + faceDepth * t} />
        ))}
      </g>
    </g>
  );
}

/** A wooden rack of wells. Deliberately different apparatus from a clamp,
 *  not a re-skin of it: a rack is what a real lab uses for a tube it is NOT
 *  heating, and putting a flame beside one would be a safety issue — which
 *  is why standAllowsBurner() refuses a burner here. */
export function TubeRack2D({ layer }: { layer: Layer }): React.JSX.Element {
  // Wide enough for four slots at RACK_SLOT_PITCH (0.40 × PX_SCALE = 44px
  // apart) plus a tube's width and a margin at each end: 3 × 44 + 25 + 2 × 9.
  // The front rail is the thinnest board here on purpose: it only has to hold
  // a tube upright, and every millimetre of it hides a millimetre of the tube
  // behind it — which is the one thing on this apparatus worth looking at.
  const W = 176, H = 52, RAIL = 7, TRAY = 8, POST = 9;
  // How deep the rack is front-to-back. Boards lie flat, so this is what their
  // top faces are: the depth, foreshortened. Without them the rack was a
  // wireframe rectangle — the one piece of apparatus with no thickness at all.
  const DEPTH = 46, face = DEPTH * VIEW_TILT;
  // The tray and posts are behind the tube; the top rail's FRONT face is in
  // front of it, so the tube visibly passes through the rack rather than
  // standing beside a board. The rail's TOP face goes behind, which is what
  // puts the tube through a well instead of in front of one.
  if (layer === 'behind') {
    return (
      <g>
        <rect x={-W / 2} y={-TRAY - face} width={W} height={TRAY + face} rx={2} fill={WOOD} stroke={LINE} strokeWidth={1.5} />
        <rect x={-W / 2} y={-TRAY - face} width={W} height={face} rx={2} fill={WOOD_LIGHT} opacity={0.5} />
        <rect x={-W / 2} y={-H} width={POST} height={H - TRAY} rx={1.5} fill={WOOD_DARK} stroke={LINE} strokeWidth={1.5} />
        <rect x={W / 2 - POST} y={-H} width={POST} height={H - TRAY} rx={1.5} fill={WOOD_DARK} stroke={LINE} strokeWidth={1.5} />
        <rect x={-W / 2} y={-H - face} width={W} height={face} rx={1.5} fill={WOOD_LIGHT} stroke={LINE} strokeWidth={1.5} />
      </g>
    );
  }
  return (
    <g>
      <rect x={-W / 2} y={-H} width={W} height={RAIL} rx={1.5} fill={WOOD} stroke={LINE} strokeWidth={1.5} />
      {/* Lit top edge and shaded bottom one, thinned with the board — at 2px
          each they would have met in the middle and left no wood between. */}
      <rect x={-W / 2 + 2} y={-H + 1.25} width={W - 4} height={1.25} fill={WOOD_LIGHT} />
      <rect x={-W / 2 + 2} y={-H + RAIL - 2.25} width={W - 4} height={1.25} fill={WOOD_DARK} />
    </g>
  );
}

export interface RodClamp {
  /** Y of the grip point in column space (negative = above the bench). */
  y: number;
  /** Half-width of the vessel where it is gripped, in px. */
  gripHalfWidthPx: number;
}

/**
 * The upright-rod stands: a weighted base, a vertical rod off to one side and
 * a boss clamp per vessel. One clamp is an ordinary retort stand; two clamps
 * at different heights on the same rod is the multi-level setup.
 */
export function RodStand2D({ rodXPx, rodTopPx, clamps, layer }: {
  rodXPx: number; rodTopPx: number; clamps: RodClamp[]; layer: Layer;
}): React.JSX.Element {
  const BASE_H = 11;
  // Base and rod stand behind the glassware; the clamp reaches around the
  // FRONT of it, which is the only way a grip reads as gripping.
  if (layer === 'behind') {
    return (
      <g>
        {/* Weighted base — deep enough not to look like the rod would topple.
            A plate lying flat, so its top face is visible above the front
            edge rather than the whole thing being a trapezoid seen edge-on. */}
        <g transform={`translate(${rodXPx}, 0)`}>
          <path
            d={`M -44,0 L 44,0 L 36,${-BASE_H} L -36,${-BASE_H} Z`}
            fill={STEEL} stroke={LINE} strokeWidth={1.5} strokeLinejoin="round"
          />
          <path
            d={`M -36,${-BASE_H} L 36,${-BASE_H} L 30,${-BASE_H - 44 * VIEW_TILT} L -30,${-BASE_H - 44 * VIEW_TILT} Z`}
            fill={STEEL} stroke={LINE} strokeWidth={1.5} strokeLinejoin="round"
          />
          <path
            d={`M -34,${-BASE_H - 1.5} L 34,${-BASE_H - 1.5} L 29,${-BASE_H - 44 * VIEW_TILT + 1.5} L -29,${-BASE_H - 44 * VIEW_TILT + 1.5} Z`}
            fill={STEEL_LIGHT} opacity={0.3}
          />
        </g>
        {/* Every clamp's FAR half, drawn here so the vessel is painted over it
            — the glass then sits INSIDE the ring or the jaws, which is the
            whole difference between a clamp and a bracket laid against the
            side of it. The near halves are in the 'front' layer below. */}
        {clamps.map((clamp, i) => (
          <path
            key={i}
            d={backArcPath(clamp.y, collarRadius(clamp), collarRadius(clamp) * VIEW_TILT)}
            fill="none" stroke={STEEL} strokeWidth={collarWeight(clamp)} strokeLinecap="round"
          />
        ))}
        <rect x={rodXPx - 4.5} y={-rodTopPx} width={9} height={rodTopPx - BASE_H} rx={1.5} fill={STEEL} stroke={LINE} strokeWidth={1.5} />
        <rect x={rodXPx - 2.5} y={-rodTopPx + 3} width={2} height={rodTopPx - BASE_H - 6} fill={STEEL_LIGHT} opacity={0.7} />
      </g>
    );
  }
  return (
    <g>
      {clamps.map((clamp, i) => {
        const isRing = clamp.gripHalfWidthPx > RING_CLAMP_THRESHOLD_PX;
        const radius = collarRadius(clamp);
        return (
          <g key={i} transform={`translate(0, ${clamp.y})`}>
            {/* boss — the block that bolts the arm to the rod */}
            <rect x={rodXPx - 9} y={-9} width={18} height={18} rx={2} fill={STEEL} stroke={LINE} strokeWidth={1.5} />
            <rect x={rodXPx + 7} y={-3.5} width={8} height={7} rx={1.5} fill={STEEL} stroke={LINE} strokeWidth={1.2} />
            {/* arm reaching in to meet the collar */}
            <line x1={rodXPx - 9} y1={0} x2={radius * Math.sign(rodXPx || 1)} y2={0} stroke={STEEL} strokeWidth={5} strokeLinecap="round" />
            {/* The near half of the collar. A ring and a jaw clamp are the
                same thing at this eye height — a circle round the vessel, so
                an ellipse — and differ in weight and in what they carry: a
                ring is thin and the vessel rests IN it, a jaw is heavier and
                pinches, with a pad either side. The old jaw was a C opening
                toward the rod, which put its back inside the glass and read
                as a bracket hooked onto one side of the neck. */}
            <path
              d={frontArcPath(0, radius, radius * VIEW_TILT)}
              fill="none" stroke={STEEL} strokeWidth={collarWeight(clamp)} strokeLinecap="round"
            />
            {!isRing && (
              <>
                <rect x={-radius - 4} y={-5.5} width={8} height={11} rx={2.5} fill={STEEL} stroke={LINE} strokeWidth={1} />
                <rect x={radius - 4} y={-5.5} width={8} height={11} rx={2.5} fill={STEEL} stroke={LINE} strokeWidth={1} />
              </>
            )}
          </g>
        );
      })}
    </g>
  );
}
